/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { batchTestSwaggerToBatchTestStore } from 'src/luis/projects/testPane/services/mappers';
import { BatchTestDataPointSwagger } from 'src/luis/projects/testPane/types/swagger/BatchTestDataPointSwagger';
import { BatchTestSwagger } from 'src/luis/projects/testPane/types/swagger/BatchTestSwagger';
import { LuisId } from 'src/luis/types/Resource';
import { BatchTestStore } from 'src/luis/types/stores/testPane/BatchTestStore';

/**
 * Represents a service to perform CRUD operations for batch test.
 *
 * @param appId The app id to get the batchTest for.
 */
export const getBatchTestService = createService(({ context, getEnvironment }) => {
    const { webApiUrl, isProduction } = getEnvironment();
    const { appId } = context;

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Gets the batch tests available for the current application.
     *
     * @returns Batch test store array.
     */
    const getBatchTests = async (): Promise<BatchTestStore[]> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(<string>appId)
            .resource('testDatasets')
            .build();

        const { data: batchTestSwagger } = await httpService.get<BatchTestSwagger[]>(url, apiOptions);

        return batchTestSwagger.map(batchTestSwaggerToBatchTestStore);
    };

    /**
     * Adds new batch test to the current application.
     *
     * @param param0.testName The name of the test to be deleted.
     * @param param0.testName The name of the test to be deleted.
     * @returns The id of the added test.
     */
    const addBatchTest = async ({
        testName,
        datasetItems
    }: {
        testName: string;
        datasetItems: BatchTestDataPointSwagger[];
    }): Promise<string> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(<string>appId)
            .resource('testDatasets')
            .queryParam('dataSetName', testName)
            .build();
        const { data: testId } = await httpService.post<string>(url, datasetItems, apiOptions);

        return testId;
    };

    /**
     * Deletes an batch test from the current application.
     *
     * @param param0.testId The id of the test to be deleted.
     */
    const deleteBatchTest = async ({ testId }: { testId: LuisId }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(<string>appId)
            .resource('testDatasets', testId)
            .build();

        await httpService.delete<void>(url, apiOptions);
    };

    /**
     * Update an batch test with new data.
     *
     * @param param0.newBatchTest The new data of the batch test to be update.
     */
    const updateBatchTest = async ({ newBatchTest }: { newBatchTest: BatchTestSwagger }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(<string>appId)
            .resource('testDatasets', newBatchTest.id)
            .build();

        await httpService.put<void>(url, newBatchTest, apiOptions);
    };

    /**
     * Rename an batch test.
     *
     * @param param0.newBatchTest The new data of the batch test to be update.
     */
    const renameBatchTest = async ({ newBatchTest }: { newBatchTest: BatchTestSwagger }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(<string>appId)
            .resource('testDatasets', newBatchTest.id)
            .resource('rename')
            .build();

        await httpService.put<void>(url, newBatchTest, apiOptions);
    };

    /**
     * Exports the app to be downloaded on the user machine.
     *
     * @param param0.testId The id of the test to be update.
     */
    const exportBatchTest = async ({ testId, testName }: { testId: LuisId; testName: string }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(<string>appId)
            .resource('testDatasets', testId)
            .resource('download')
            .build();

        await fileService.download(url, testName, apiOptions);
    };

    /**
     * Runs the batch test with the given id.
     *
     * @param param0.testId The id of the test to be update.
     * @param param0.newBatchTest The new data of the batch test to be update.
     * @returns The batch test store updated with the latest results
     */
    const runBatchTest = async ({ testId, appVersion }: { testId: LuisId; appVersion: string }): Promise<BatchTestDataPointSwagger[]> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .app(<string>appId)
            .version(<string>appVersion)
            .resource('testDatasets', testId)
            .resource('run')
            .build();

        const { data: datasetItems } = await httpService.get<BatchTestDataPointSwagger[]>(url, apiOptions);

        if (!datasetItems.length) {
            throw new Error('The dataset results is empty!');
        }

        return datasetItems;
    };

    return { addBatchTest, deleteBatchTest, updateBatchTest, renameBatchTest, exportBatchTest, runBatchTest, getBatchTests };
});
